import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { type Locale, type Translations, getTranslations, LIVE_LOCALES, isLocale } from './locales';

interface I18nContextValue {
  locale: Locale;
  t: Translations;
  setLocale: (l: Locale) => void;
  toggleLocale: () => void;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children, localeOverride }: { children: ReactNode; localeOverride?: Locale }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const saved = localStorage.getItem('sudoku_lang');
    return saved && isLocale(saved) ? saved : 'tr';
  });

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    localStorage.setItem('sudoku_lang', l);
  }, []);

  // Sadece canlıya çıkmış diller arasında döner (toggle butonu / dropdown'sız tek tık).
  const toggleLocale = useCallback(() => {
    const idx = LIVE_LOCALES.indexOf(locale);
    const next = LIVE_LOCALES[(idx + 1) % LIVE_LOCALES.length] ?? 'tr';
    setLocale(next);
  }, [locale, setLocale]);

  const activeLocale = localeOverride ?? locale;
  const t = getTranslations(activeLocale);

  return (
    <I18nContext.Provider value={{ locale: activeLocale, t, setLocale, toggleLocale }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}
